/**
 * RepoTable — virtualized Repo_List_View (Req 3, 10.1, 10.3, 12.5).
 *
 * Renders the repositories of the selected Scan_Run as a single-row-per-repo
 * table built on TanStack Table (column/row model) and TanStack Virtual (row
 * virtualization). Responsibilities, mapped to the design "RepoTable" row and
 * the requirements:
 *
 *  - One row per {@link RepoSummary}; the underlying virtualizer keeps the DOM
 *    small enough to handle 1000+ rows (Req 3.1, 12.5).
 *  - Seven metadata columns — repository name, primary language, Status,
 *    visibility, archived state, size, and last activity (`pushedAt`)
 *    (Req 3.2) — each rendering a placeholder when the source value is
 *    `null`/absent (Req 3.3, 10.1).
 *  - Eight capability present/absent indicators (Req 3.4).
 *  - Status rendered with a visual treatment that distinguishes `failed`/
 *    `partial` from `success` (Req 10.3).
 *  - The displayed repository count (Req 3.5) and an empty-state message with a
 *    count of 0 and no data rows (Req 3.6).
 *  - Row activation opening the detail view via `onActivateRow` (Req 3.7).
 *  - Sortable headers for the five sortable columns, wired to report a new
 *    {@link SortState} via `onSortChange`: a column not currently sorted starts
 *    ascending, and re-activating the active column toggles direction (Req 7.6).
 *
 * The component is presentational and does not sort or filter: it renders the
 * `rows` it is given (already the `applyQuery` result owned by the parent) and
 * reports sort-header activations upward. Only the viewport rows plus a small
 * overscan buffer (well under the 50-row cap of Req 12.5) are rendered.
 */

import { useMemo, useRef } from "react";
import type { CSSProperties } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type RowData,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import type {
  CapabilityKey,
  LibraryRepoEntry,
  RepoSummary,
  ScanStatus,
  SortColumn,
  SortState,
} from "../types";
import { capabilityColor, statusPillColor } from "../styles/mapping";
import { joinLibraryEntry } from "../lib/query";

// Attach per-column presentation metadata (which sort key the header maps to,
// its width, alignment, and whether it is a capability indicator) directly to
// the TanStack column definitions.
declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** The sort key this header toggles, when the column is sortable (Req 7). */
    sortColumn?: SortColumn;
    /** Fixed pixel width for the flex cell. */
    width: number;
    /** Horizontal alignment of the cell content. */
    align?: "left" | "right" | "center";
    /** True for the eight capability indicator columns (Req 3.4). */
    isCapability?: boolean;
  }
}

// ---------------------------------------------------------------------------
// Sort toggling (Req 7.6)
// ---------------------------------------------------------------------------

/**
 * Compute the next {@link SortState} when a sortable header is activated
 * (Req 7.6): selecting a column that is not the active one applies ascending
 * order; re-activating the active column toggles between ascending and
 * descending. Pure — does not mutate `current`.
 */
export function toggleSort(current: SortState, column: SortColumn): SortState {
  if (current.column === column) {
    return {
      column,
      direction: current.direction === "asc" ? "desc" : "asc",
    };
  }
  return { column, direction: "asc" };
}

// ---------------------------------------------------------------------------
// Cell formatting helpers (placeholders for null/absent — Req 3.3, 10.1)
// ---------------------------------------------------------------------------

/** Glyph shown wherever a metadata value is `null`/absent (Req 3.3, 10.1). */
const PLACEHOLDER = "\u2014"; // em dash

/** Muted placeholder span with an accessible label explaining the absence. */
function Placeholder({ label }: { label: string }) {
  return (
    <span style={placeholderStyle} title={label} aria-label={label}>
      {PLACEHOLDER}
    </span>
  );
}

/** Render a possibly-null string value, falling back to a placeholder. */
function renderText(value: string | null, missingLabel: string) {
  if (value === null || value === "") {
    return <Placeholder label={missingLabel} />;
  }
  return <span title={value}>{value}</span>;
}

/**
 * Render a Library classification value (Req 18.1, 18.6). A `null` value means
 * the row has no joined Library entry — there is no Library data for it — and
 * renders the same em-dash placeholder as today's metadata columns. A present
 * value is always rendered explicitly, INCLUDING the literal `"unknown"` /
 * `"unavailable"` markers (never blanked), shown muted so they read as the
 * absence of a classification rather than data (Req 18.6).
 */
function renderLibraryValue(value: string | null, missingLabel: string) {
  if (value === null || value === "") {
    return <Placeholder label={missingLabel} />;
  }
  const isMarker = value === "unknown" || value === "unavailable";
  return (
    <span title={value} style={isMarker ? libraryMarkerStyle : undefined}>
      {value}
    </span>
  );
}

/** Render a possibly-null boolean attribute as Yes/No, else a placeholder. */
function renderBool(value: boolean | null, missingLabel: string) {
  if (value === null) return <Placeholder label={missingLabel} />;
  return <span>{value ? "Yes" : "No"}</span>;
}

/** Render a possibly-null numeric size, else a placeholder. */
function renderSize(value: number | null) {
  if (value === null) return <Placeholder label="size unavailable" />;
  return <span title={`${value}`}>{value.toLocaleString()}</span>;
}

/**
 * Render an ISO-8601 `pushedAt` timestamp. Shows the calendar date for
 * compactness with the full instant in the tooltip; placeholder when absent.
 */
function renderPushedAt(value: string | null) {
  if (value === null || value === "") {
    return <Placeholder label="last activity unavailable" />;
  }
  const datePart = value.split("T")[0] ?? value;
  return <span title={value}>{datePart}</span>;
}

// ---------------------------------------------------------------------------
// Status + capability indicators (Req 10.3, 3.4)
// ---------------------------------------------------------------------------

/**
 * Per-status background tint; `failed`/`partial` are distinct from `success`
 * (Req 10.3). The pill's semantic foreground/border color is sourced from the
 * pure `statusPillColor` mapping (success→green, partial→orange,
 * failed→danger-red — Req 25.1–25.3).
 */
const STATUS_BG: Record<ScanStatus, string> = {
  success: "var(--green-50)",
  partial: "var(--orange-50)",
  failed: "var(--neutral-100)",
};

/** Status pill: tinted background with the semantic status color (Req 25.1–25.3). */
function StatusBadge({ status }: { status: ScanStatus }) {
  const color = statusPillColor(status);
  return (
    <span
      data-status={status}
      style={{
        ...statusBadgeStyle,
        background: STATUS_BG[status],
        color,
        borderColor: color,
      }}
    >
      {status}
    </span>
  );
}

/** A present/absent capability indicator (Req 3.4, 25.4). */
function CapabilityCell({ present, label }: { present: boolean; label: string }) {
  return (
    <span
      role="img"
      aria-label={`${label}: ${present ? "present" : "absent"}`}
      title={`${label}: ${present ? "present" : "absent"}`}
      style={{
        ...(present ? capabilityPresentStyle : capabilityAbsentStyle),
        color: capabilityColor(present),
      }}
    >
      {present ? "\u2713" : PLACEHOLDER}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const columnHelper = createColumnHelper<RepoSummary>();

/** Short header label for each of the eight capability columns (Req 3.4). */
const CAPABILITY_COLUMNS: { key: CapabilityKey; header: string; label: string }[] = [
  { key: "hasCicd", header: "CI/CD", label: "CI/CD" },
  { key: "hasDockerfile", header: "Docker", label: "Dockerfile" },
  { key: "hasIac", header: "IaC", label: "Infrastructure-as-code" },
  { key: "hasApiSpec", header: "API", label: "API specification" },
  { key: "hasReadme", header: "README", label: "README" },
  { key: "hasOwner", header: "Owner", label: "Owner" },
  { key: "hasDomainMatches", header: "Domain", label: "Domain matches" },
  { key: "hasErrors", header: "Errors", label: "Errors" },
];

const columns = [
  // Seven metadata columns (Req 3.2). Sortable ones carry a `sortColumn` (Req 7).
  columnHelper.accessor("repoName", {
    header: "Repository",
    meta: { sortColumn: "repoName", width: 240 },
    cell: (info) => <span title={info.getValue()}>{info.getValue()}</span>,
  }),
  columnHelper.accessor("primaryLanguage", {
    header: "Language",
    meta: { sortColumn: "primaryLanguage", width: 130 },
    cell: (info) => renderText(info.getValue(), "language unavailable"),
  }),
  columnHelper.accessor("status", {
    header: "Status",
    meta: { sortColumn: "status", width: 110 },
    cell: (info) => <StatusBadge status={info.getValue()} />,
  }),
  columnHelper.accessor("visibility", {
    header: "Visibility",
    meta: { width: 120 },
    cell: (info) => renderText(info.getValue(), "visibility unavailable"),
  }),
  columnHelper.accessor("archived", {
    header: "Archived",
    meta: { width: 100, align: "center" },
    cell: (info) => renderBool(info.getValue(), "archived state unavailable"),
  }),
  columnHelper.accessor("size", {
    header: "Size",
    meta: { sortColumn: "size", width: 110, align: "right" },
    cell: (info) => renderSize(info.getValue()),
  }),
  columnHelper.accessor("pushedAt", {
    header: "Last activity",
    meta: { sortColumn: "pushedAt", width: 140 },
    cell: (info) => renderPushedAt(info.getValue()),
  }),
  // Eight capability indicator columns (Req 3.4).
  ...CAPABILITY_COLUMNS.map((cap) =>
    columnHelper.accessor((row) => row[cap.key], {
      id: cap.key,
      header: cap.header,
      meta: { width: 80, align: "center", isCapability: true },
      cell: (info) => (
        <CapabilityCell present={info.getValue() as boolean} label={cap.label} />
      ),
    }),
  ),
];

// ---------------------------------------------------------------------------
// Optional Repo Knowledge Library columns (Req 18.1, 18.6) — ADDITIVE
// ---------------------------------------------------------------------------
//
// These six columns are appended ONLY when the parent supplies a non-empty
// `libraryByKey` join map (i.e. a Library document exists for the run). When it
// is absent or empty the table renders exactly the columns above — today's
// behavior — so the feature degrades gracefully (graceful absence). Each cell
// looks the row's entry up by `joinLibraryEntry` (keyed on `detailKey` via the
// row id); a row with no matching entry renders the existing em-dash
// placeholder, and the literal `"unknown"`/`"unavailable"` markers are rendered
// explicitly (Req 18.6). The columns are NON-sortable, so no new `SortColumn`
// value is introduced and the sort types/logic are untouched.

/** The six optional Library columns, in display order. */
const LIBRARY_COLUMNS: {
  id: string;
  header: string;
  width: number;
  missingLabel: string;
  value: (entry: LibraryRepoEntry) => string;
}[] = [
  {
    id: "libraryRepoType",
    header: "Repo Type",
    width: 140,
    missingLabel: "repo type unavailable",
    value: (entry) => entry.repoType,
  },
  {
    id: "libraryLifecycle",
    header: "Lifecycle",
    width: 120,
    missingLabel: "lifecycle unavailable",
    value: (entry) => entry.lifecycleStatus,
  },
  {
    id: "libraryOwningTeam",
    header: "Owning Team",
    width: 150,
    missingLabel: "owning team unavailable",
    value: (entry) => entry.owningTeam,
  },
  {
    id: "libraryApplication",
    header: "Application",
    width: 150,
    missingLabel: "application unavailable",
    value: (entry) => entry.application,
  },
  {
    id: "libraryProduct",
    header: "Product",
    width: 150,
    missingLabel: "product unavailable",
    value: (entry) => entry.product,
  },
  {
    id: "libraryShortDescription",
    header: "Short Description",
    width: 280,
    missingLabel: "description unavailable",
    value: (entry) => entry.shortDescription,
  },
];

/**
 * Build the six optional Library column definitions bound to the given join
 * map. Each is a display column (no accessor / no sort key) whose cell resolves
 * the row's `LibraryRepoEntry` by `repoName` + `scanId`; an unmatched row shows
 * the em-dash placeholder and matched rows render the value verbatim, including
 * the explicit `"unknown"`/`"unavailable"` markers (Req 18.1, 18.6).
 */
function buildLibraryColumns(
  byKey: Map<string, LibraryRepoEntry>,
): ColumnDef<RepoSummary>[] {
  return LIBRARY_COLUMNS.map((col) =>
    columnHelper.display({
      id: col.id,
      header: col.header,
      meta: { width: col.width },
      cell: (info) => {
        const entry = joinLibraryEntry(info.row.original, byKey);
        return renderLibraryValue(
          entry ? col.value(entry) : null,
          col.missingLabel,
        );
      },
    }),
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Props for {@link RepoTable}. */
export interface RepoTableProps {
  /**
   * Rows to display, already searched/filtered/sorted by the parent
   * (`applyQuery` result). The table renders them in the given order (Req 7.7).
   */
  rows: RepoSummary[];
  /** The active sort selection, used to render header sort indicators (Req 7). */
  sort: SortState;
  /** Called with the next sort state when a sortable header is activated (Req 7.6). */
  onSortChange: (sort: SortState) => void;
  /** Called with the row's summary when a row is activated (Req 3.7). */
  onActivateRow: (row: RepoSummary) => void;
  /**
   * ADDITIVE (Req 18.1, 18.6): the per-run Repo Knowledge Library entries keyed
   * by `repoName` + `scanId` (from `useRunLoader.libraryByKey`). When omitted or
   * empty — an older run with no Library document — NO Library columns are
   * rendered and the table is identical to today's. When present and non-empty,
   * six optional classification columns are appended.
   */
  libraryByKey?: Map<string, LibraryRepoEntry>;
}

/** Empty-state message shown when zero rows are displayed (Req 3.6). */
export const EMPTY_MESSAGE = "No repositories to display.";

/** Estimated row height (px) for the virtualizer. */
const ROW_HEIGHT = 40;
/**
 * Rows rendered beyond the viewport on each side. Kept well under the 50-row
 * cap so total off-screen rendered rows stay within budget (Req 12.5).
 */
const OVERSCAN = 12;

/**
 * Virtualized repository table. Renders the seven metadata columns and eight
 * capability indicators for each row, a sortable header row, the displayed
 * count, and an empty state — virtualizing the body so only viewport rows plus
 * a small buffer are in the DOM (Req 12.5).
 */
export default function RepoTable({
  rows,
  sort,
  onSortChange,
  onActivateRow,
  libraryByKey,
}: RepoTableProps) {
  // ADDITIVE (Req 18.1, 18.6): append the six optional Library columns only
  // when a non-empty join map is supplied; otherwise the column set — and thus
  // the rendered table — is byte-for-byte today's (graceful absence).
  const hasLibrary = libraryByKey !== undefined && libraryByKey.size > 0;
  const tableColumns = useMemo(
    () =>
      hasLibrary && libraryByKey
        ? [...columns, ...buildLibraryColumns(libraryByKey)]
        : columns,
    [hasLibrary, libraryByKey],
  );

  const table = useReactTable({
    data: rows,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    // The parent owns ordering (via applyQuery); the table never re-sorts.
    manualSorting: true,
    getRowId: (row) => row.detailKey,
  });

  const tableRows = table.getRowModel().rows;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalHeight = rowVirtualizer.getTotalSize();
  const headerGroups = table.getHeaderGroups();

  return (
    <section aria-label="Repository list">
      {/* Section header — bold title, subtitle, right-aligned count (Req 3.5).
          Mirrors the reference "KNOWN ASSET CONTEXT — N ASSETS" layout. */}
      <header className="ds-section-header">
        <div>
          <h2 className="ds-section-header__title">Repository list</h2>
          <p className="ds-section-header__subtitle">
            Click a row to load it into the detail view.
          </p>
        </div>
        <p className="ds-section-header__count">
          <strong data-testid="repo-count">{rows.length}</strong>{" "}
          {rows.length === 1 ? "repository" : "repositories"}
        </p>
      </header>

      <div ref={scrollContainerRef} style={scrollContainerStyle}>
        <table style={tableStyle} role="table">
          <thead style={theadStyle}>
            {headerGroups.map((headerGroup) => (
              <tr key={headerGroup.id} style={headerRowStyle}>
                {headerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta;
                  const width = meta?.width ?? 120;
                  const align = meta?.align ?? "left";
                  const sortColumn = meta?.sortColumn;
                  const label = flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  );

                  // Sortable header: a button that reports the next sort state
                  // and exposes the current direction via aria-sort (Req 7.6).
                  if (sortColumn) {
                    const isActive = sort.column === sortColumn;
                    const ariaSort: "ascending" | "descending" | "none" = isActive
                      ? sort.direction === "asc"
                        ? "ascending"
                        : "descending"
                      : "none";
                    const indicator = isActive
                      ? sort.direction === "asc"
                        ? "\u25b2"
                        : "\u25bc"
                      : "";
                    return (
                      <th
                        key={header.id}
                        scope="col"
                        aria-sort={ariaSort}
                        style={{ ...headerCellStyle, width, textAlign: align }}
                      >
                        <button
                          type="button"
                          onClick={() => onSortChange(toggleSort(sort, sortColumn))}
                          style={sortButtonStyle}
                        >
                          <span>{label}</span>
                          <span aria-hidden="true" style={sortIndicatorStyle}>
                            {indicator}
                          </span>
                        </button>
                      </th>
                    );
                  }

                  // Non-sortable header (visibility, archived, capabilities).
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      style={{ ...headerCellStyle, width, textAlign: align }}
                    >
                      {label}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody style={{ ...tbodyStyle, height: `${totalHeight}px` }}>
            {tableRows.length === 0 ? null : (
              virtualRows.map((virtualRow) => {
                const row = tableRows[virtualRow.index];
                return (
                  <tr
                    key={row.id}
                    data-index={virtualRow.index}
                    onClick={() => onActivateRow(row.original)}
                    onKeyDown={(event) => {
                      // Keyboard row activation (Req 3.7).
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onActivateRow(row.original);
                      }
                    }}
                    tabIndex={0}
                    role="row"
                    style={{
                      ...bodyRowStyle,
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const meta = cell.column.columnDef.meta;
                      const width = meta?.width ?? 120;
                      const align = meta?.align ?? "left";
                      // First column (repository name) rendered bold (Req 24.4).
                      const isFirstColumn = cell.column.id === "repoName";
                      return (
                        <td
                          key={cell.id}
                          role="cell"
                          style={{
                            ...bodyCellStyle,
                            width,
                            fontWeight: isFirstColumn
                              ? "var(--fw-bold)"
                              : undefined,
                            color: isFirstColumn
                              ? "var(--neutral-900)"
                              : undefined,
                            justifyContent:
                              align === "right"
                                ? "flex-end"
                                : align === "center"
                                  ? "center"
                                  : "flex-start",
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Empty-state message; no data rows are rendered (Req 3.6). */}
        {rows.length === 0 && (
          <p role="status" style={emptyStateStyle}>
            {EMPTY_MESSAGE}
          </p>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const scrollContainerStyle: CSSProperties = {
  height: "600px",
  overflow: "auto",
  border: "1px solid var(--neutral-200)",
  borderRadius: "8px",
  position: "relative",
  background: "var(--neutral-0)",
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.02)",
};

const tableStyle: CSSProperties = {
  display: "grid",
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "var(--text-small)",
  fontFamily: "var(--font-family)",
};

const theadStyle: CSSProperties = {
  display: "grid",
  position: "sticky",
  top: 0,
  zIndex: 1,
  background: "var(--neutral-50)",
  borderTopLeftRadius: "8px",
  borderTopRightRadius: "8px",
};

const headerRowStyle: CSSProperties = {
  display: "flex",
  width: "100%",
};

const headerCellStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
  padding: "0.5rem 0.75rem",
  fontWeight: "var(--fw-semibold)",
  color: "var(--blue-700)",
  borderBottom: "1px solid var(--neutral-300)",
  boxSizing: "border-box",
};

const sortButtonStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.25rem",
  background: "none",
  border: "none",
  padding: 0,
  margin: 0,
  font: "inherit",
  fontWeight: "var(--fw-semibold)",
  cursor: "pointer",
  color: "inherit",
};

const sortIndicatorStyle: CSSProperties = {
  fontSize: "0.625rem",
  width: "0.75rem",
};

const tbodyStyle: CSSProperties = {
  display: "grid",
  position: "relative",
  width: "100%",
};

const bodyRowStyle: CSSProperties = {
  display: "flex",
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  alignItems: "center",
  borderBottom: "1px solid var(--neutral-200)",
  cursor: "pointer",
  boxSizing: "border-box",
  color: "var(--neutral-800)",
};

const bodyCellStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
  padding: "0.375rem 0.75rem",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  boxSizing: "border-box",
};

const statusBadgeStyle: CSSProperties = {
  display: "inline-block",
  padding: "0.125rem 0.5rem",
  border: "1px solid",
  borderRadius: "999px",
  fontSize: "var(--text-tiny)",
  fontWeight: "var(--fw-semibold)",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
};

const placeholderStyle: CSSProperties = {
  color: "var(--neutral-500)",
};

/** Muted treatment for explicit `"unknown"`/`"unavailable"` Library markers (Req 18.6). */
const libraryMarkerStyle: CSSProperties = {
  color: "var(--neutral-500)",
  fontStyle: "italic",
};

const capabilityPresentStyle: CSSProperties = {
  fontWeight: "var(--fw-bold)",
};

const capabilityAbsentStyle: CSSProperties = {};

const emptyStateStyle: CSSProperties = {
  padding: "2rem 1rem",
  textAlign: "center",
  color: "var(--neutral-600)",
};
